import React, { useState, useEffect } from 'react';
import {
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Divider,
    Box,
    Autocomplete,
    InputAdornment,
    Chip,
    Alert,
    CircularProgress,
    useTheme,
    alpha,
    Card,
    CardContent
} from '@mui/material';
import {
    Thermostat as ThermostatIcon,
    WaterDrop as HumidityIcon,
    Engineering as EngineeringIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { cleanRoomClasses } from './constants';
import { getCalibrationStatus } from './utils';
import { supabase } from '../../../../../supabaseClient';
import dayjs from 'dayjs';

export default function EnvironmentStep({ 
    formData, 
    setFormData, 
    companyId,
    onValidationChange,
    showSnackbar,
    batchTimeFormat = '12hour'
}) {
    const theme = useTheme();
    const [equipmentList, setEquipmentList] = useState([]);
    const [equipmentLoading, setEquipmentLoading] = useState(false);

    // Validation logic
    const isStepValid = () => {
        return formData.equipment_name && formData.room_temperature_c;
    };

    // Notify parent of validation status when data changes
    useEffect(() => {
        const timer = setTimeout(() => {
            if (onValidationChange) {
                onValidationChange('environment', isStepValid());
            }
        }, 100);
        return () => clearTimeout(timer);
    }, [formData.equipment_name, formData.room_temperature_c]);

    // Fetch equipment when component mounts
    useEffect(() => {
        if (companyId && companyId !== 'undefined') {
            fetchEquipment();
        }
    }, [companyId]);

    const fetchEquipment = async () => {
        if (!companyId || companyId === 'undefined') {
            return;
        }
        
        setEquipmentLoading(true);
        try {
            const { data, error } = await supabase
                .from('qc_equipment_calibration')
                .select('*')
                .eq('company_id', companyId)
                .order('equipment_name');

            if (error) throw error;
            setEquipmentList(data || []);
        } catch (error) {
            showSnackbar?.('Error loading equipment list: ' + error.message, 'error');
        } finally {
            setEquipmentLoading(false);
        }
    };

    const handleEquipmentSelect = (equipment) => {
        if (!equipment) return;
        
        const calibStatus = getCalibrationStatus(equipment.calibration_due_date);
        setFormData({
            ...formData,
            equipment_id: equipment.equipment_id,
            equipment_name: equipment.equipment_name,
            equipment_model: equipment.equipment_model,
            equipment_serial: equipment.equipment_serial,
            calibration_status: calibStatus.status === 'overdue' ? 'overdue' : 'calibrated',
            calibration_date: equipment.calibration_date ? dayjs(equipment.calibration_date) : null,
            calibration_due_date: equipment.calibration_due_date ? dayjs(equipment.calibration_due_date) : null
        });
    };

    const handleTemperatureChange = (value) => {
        if (formData.temp_unit === 'celsius') {
            setFormData({...formData, room_temperature_c: value, temp_display_value: value});
        } else {
            // Store the Fahrenheit display value and calculate Celsius for storage
            const celsiusValue = value ? ((value - 32) * 5/9).toFixed(1) : '';
            setFormData({...formData, room_temperature_c: celsiusValue, temp_display_value: value});
        }
    };

    const handleTempUnitChange = (unit) => {
        if (unit === 'celsius') {
            // Convert from Fahrenheit to Celsius if we have a display value
            const celsiusValue = formData.temp_display_value ? ((formData.temp_display_value - 32) * 5/9).toFixed(1) : formData.room_temperature_c;
            setFormData({...formData, temp_unit: unit, room_temperature_c: celsiusValue, temp_display_value: celsiusValue});
        } else {
            // Convert from Celsius to Fahrenheit
            const fahrenheitValue = formData.room_temperature_c ? ((formData.room_temperature_c * 9/5) + 32).toFixed(1) : '';
            setFormData({...formData, temp_unit: unit, temp_display_value: fahrenheitValue});
        }
    };

    const handleHumidityChange = (value) => {
        setFormData({...formData, room_humidity_percent: value});
    };

    const handleCleanRoomChange = (value) => {
        setFormData({...formData, clean_room_classification: value});
    };

    const handleEquipmentNameChange = (value) => {
        setFormData({...formData, equipment_name: value});
    };

    const handleEquipmentIdChange = (value) => {
        setFormData({...formData, equipment_id: value});
    };

    const handleEquipmentModelChange = (value) => {
        setFormData({...formData, equipment_model: value});
    };

    const handleEquipmentSerialChange = (value) => {
        setFormData({...formData, equipment_serial: value});
    };

    const handleCalibrationStatusChange = (value) => {
        setFormData({...formData, calibration_status: value});
    };

    const handleCalibrationDateChange = (value) => {
        setFormData({...formData, calibration_date: value});
    };

    const handleCalibrationDueDateChange = (value) => {
        setFormData({...formData, calibration_due_date: value});
    };



    return (
        <Box sx={{ p: 4 }}>
            {/* Header */}
            <Card sx={{ 
                borderRadius: 4,
                border: `1px solid ${theme.palette.grey[400]}`,
                background: 'white',
                backdropFilter: 'blur(20px)',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                mb: 4
            }}>
                <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ 
                            p: 1.5, 
                            borderRadius: 2, 
                            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            backdropFilter: 'blur(10px)',
                            mr: 2
                        }}>
                            <ThermostatIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                        </Box>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                                Environmental & Equipment
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Record conditions and equipment details
                            </Typography>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
            
            <Grid container spacing={4}>

            {/* Validation Alert */}
            {!isStepValid() && (
                <Grid item xs={12}>
                    <Alert 
                        severity="warning" 
                        sx={{ 
                            borderRadius: 2,
                            backgroundColor: alpha(theme.palette.warning.main, 0.1),
                            border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                            '& .MuiAlert-icon': {
                                color: theme.palette.warning.main
                            }
                        }}
                    >
                        <Typography sx={{ color: theme.palette.warning.main, fontWeight: 500 }}>
                            Please complete required fields: Equipment Name and Room Temperature.
                        </Typography>
                    </Alert>
                </Grid>
            )}
            
            <Grid item xs={12} md={3}>
                <TextField
                    fullWidth
                    label="Room Temperature *"
                    name="room_temperature_c"
                    type="number"
                    value={formData.temp_unit === 'celsius' ? (formData.temp_display_value || formData.room_temperature_c || '') : (formData.temp_display_value || '')}
                    onChange={(e) => handleTemperatureChange(e.target.value)}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><ThermostatIcon /></InputAdornment>,
                        endAdornment: <InputAdornment position="end">{formData.temp_unit === 'celsius' ? '°C' : '°F'}</InputAdornment>
                    }}
                    InputLabelProps={{
                        sx: { fontWeight: 500 }
                    }}
                    error={!formData.room_temperature_c}
                    helperText={!formData.room_temperature_c ? "Temperature is required" : ""}
                    required
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                border: `1px solid ${theme.palette.grey[400]}`,
                                borderRadius: 3
                            },
                            '&:hover fieldset': {
                                border: `1px solid ${theme.palette.primary.main}`
                            },
                            '&.Mui-focused fieldset': {
                                border: `1px solid ${theme.palette.primary.main}`
                            },
                            '& input': {
                                py: 2,
                                fontWeight: 500
                            },
                            backgroundColor: 'white',
                            borderRadius: 3,
                            overflow: 'hidden',
                            backdropFilter: 'blur(10px)',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                            }
                        }
                    }}
                />
            </Grid>
            
            <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                    <InputLabel sx={{ fontWeight: 500 }}>Unit</InputLabel>
                    <Select
                        value={formData.temp_unit || 'celsius'}
                        label="Unit"
                        onChange={(e) => handleTempUnitChange(e.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                                border: `1px solid ${theme.palette.grey[400]}`,
                                borderRadius: 3
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                border: `1px solid ${theme.palette.primary.main}`
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                border: `1px solid ${theme.palette.primary.main}`
                            },
                            '& .MuiSelect-select': {
                                py: 2,
                                fontWeight: 500
                            },
                            backgroundColor: 'white',
                            borderRadius: 3,
                            overflow: 'hidden',
                            backdropFilter: 'blur(10px)',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                            }
                        }}
                    >
                        <MenuItem value="celsius" sx={{ py: 2, fontWeight: 500 }}>°C</MenuItem>
                        <MenuItem value="fahrenheit" sx={{ py: 2, fontWeight: 500 }}>°F</MenuItem>
                    </Select>
                </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
                <TextField
                    fullWidth
                    label="Room Humidity"
                    type="number"
                    value={formData.room_humidity_percent}
                    onChange={(e) => handleHumidityChange(e.target.value)}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><HumidityIcon /></InputAdornment>,
                        endAdornment: <InputAdornment position="end">%</InputAdornment>
                    }}
                    InputLabelProps={{
                        sx: { fontWeight: 500 }
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                border: `1px solid ${theme.palette.grey[400]}`,
                                borderRadius: 3
                            },
                            '&:hover fieldset': {
                                border: `1px solid ${theme.palette.primary.main}`
                            },
                            '&.Mui-focused fieldset': {
                                border: `1px solid ${theme.palette.primary.main}`
                            },
                            '& input': {
                                py: 2,
                                fontWeight: 500
                            },
                            backgroundColor: 'white',
                            borderRadius: 3,
                            overflow: 'hidden',
                            backdropFilter: 'blur(10px)',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                            }
                        }
                    }}
                />
            </Grid>
            
            <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                    <InputLabel sx={{ fontWeight: 500 }}>Clean Room Classification</InputLabel>
                    <Select
                        value={formData.clean_room_classification}
                        label="Clean Room Classification"
                        onChange={(e) => handleCleanRoomChange(e.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                                border: `1px solid ${theme.palette.grey[400]}`,
                                borderRadius: 3
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                border: `1px solid ${theme.palette.primary.main}`
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                border: `1px solid ${theme.palette.primary.main}`
                            },
                            '& .MuiSelect-select': {
                                py: 2,
                                fontWeight: 500
                            },
                            backgroundColor: 'white',
                            borderRadius: 3,
                            overflow: 'hidden',
                            backdropFilter: 'blur(10px)',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                            }
                        }}
                    >
                        {cleanRoomClasses.map(room => (
                            <MenuItem key={room.value} value={room.value} sx={{ py: 2 }}>
                                <Box sx={{ py: 0.5 }}>
                                    <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1rem' }}>
                                        {room.label}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ display: 'block', fontSize: '0.9rem', fontWeight: 500 }}>
                                        {room.description}
                                    </Typography>
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            
            <Grid item xs={12}>
                <Box sx={{ my: 4 }}>
                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                        Equipment Information
                    </Typography>
                </Box>
            </Grid>
            
            {equipmentList && equipmentList.length > 0 && (
                <Grid item xs={12}>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Select from calibrated equipment list OR enter equipment details manually
                        </Typography>
                        {equipmentLoading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <CircularProgress size={20} />
                                <Typography variant="body2">Loading equipment...</Typography>
                            </Box>
                        ) : (
                            <Autocomplete
                                options={equipmentList}
                                getOptionLabel={(option) => `${option.equipment_name} (${option.equipment_id})`}
                                value={equipmentList.find(e => e.equipment_id === formData.equipment_id) || null}
                                onChange={(e, value) => handleEquipmentSelect(value)}
                                renderInput={(params) => (
                                    <TextField 
                                        {...params} 
                                        label="Quick Select - Calibrated Equipment" 
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: <InputAdornment position="start"><EngineeringIcon /></InputAdornment>
                                        }}
                                        InputLabelProps={{
                                            ...params.InputLabelProps,
                                            sx: { fontWeight: 500 }
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    border: `1px solid ${theme.palette.grey[400]}`,
                                                    borderRadius: 3
                                                },
                                                '&:hover fieldset': {
                                                    border: `1px solid ${theme.palette.primary.main}`
                                                },
                                                '&.Mui-focused fieldset': {
                                                    border: `1px solid ${theme.palette.primary.main}`
                                                },
                                                '& input': {
                                                    py: 2,
                                                    fontWeight: 500
                                                },
                                                backgroundColor: 'white',
                                                borderRadius: 3,
                                                overflow: 'hidden',
                                                backdropFilter: 'blur(10px)',
                                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                                '&:hover': {
                                                    transform: 'translateY(-1px)',
                                                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                                                }
                                            }
                                        }}
                                    />
                                )}
                                renderOption={(props, option) => {
                                    const calibStatus = getCalibrationStatus(option.calibration_due_date);
                                    return (
                                        <Box component="li" {...props}>
                                            <Box>
                                                <Typography>{option.equipment_name}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    ID: {option.equipment_id} • Model: {option.equipment_model}
                                                </Typography>
                                                <Box sx={{ mt: 0.5 }}>
                                                    <Chip
                                                        label={calibStatus.status}
                                                        size="small"
                                                        color={calibStatus.color}
                                                        icon={calibStatus.icon}
                                                    />
                                                </Box>
                                            </Box>
                                        </Box>
                                    );
                                }}
                            />
                        )}
                    </Box>
                </Grid>
            )}
            
            <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                    Equipment Details (Required)
                </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Equipment Name *"
                    value={formData.equipment_name}
                    onChange={(e) => handleEquipmentNameChange(e.target.value)}
                    placeholder="e.g., Brookfield Viscometer, pH Meter, Balance"
                    error={!formData.equipment_name}
                    helperText={!formData.equipment_name ? "Equipment name is required" : ""}
                    required
                    InputLabelProps={{
                        sx: { fontWeight: 500 }
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                border: `1px solid ${theme.palette.grey[400]}`,
                                borderRadius: 3
                            },
                            '&:hover fieldset': {
                                border: `1px solid ${theme.palette.primary.main}`
                            },
                            '&.Mui-focused fieldset': {
                                border: `1px solid ${theme.palette.primary.main}`
                            },
                            '& input': {
                                py: 2,
                                fontWeight: 500
                            },
                            backgroundColor: 'white',
                            borderRadius: 3,
                            overflow: 'hidden',
                            backdropFilter: 'blur(10px)',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                            }
                        }
                    }}
                />
            </Grid>
            
            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Equipment ID/Serial"
                    value={formData.equipment_id}
                    onChange={(e) => handleEquipmentIdChange(e.target.value)}
                    placeholder="e.g., BRK-001, PM-205"
                    InputLabelProps={{
                        sx: { fontWeight: 500 }
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                border: `1px solid ${theme.palette.grey[400]}`,
                                borderRadius: 3
                            },
                            '&:hover fieldset': {
                                border: `1px solid ${theme.palette.primary.main}`
                            },
                            '&.Mui-focused fieldset': {
                                border: `1px solid ${theme.palette.primary.main}`
                            },
                            '& input': {
                                py: 2,
                                fontWeight: 500
                            },
                            backgroundColor: 'white',
                            borderRadius: 3,
                            overflow: 'hidden',
                            backdropFilter: 'blur(10px)',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                            }
                        }
                    }}
                />
            </Grid>
            
            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Equipment Model"
                    value={formData.equipment_model}
                    onChange={(e) => handleEquipmentModelChange(e.target.value)}
                    placeholder="e.g., RV DV-II+, pH 3110"
                    InputLabelProps={{
                        sx: { fontWeight: 500 }
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                border: `1px solid ${theme.palette.grey[400]}`,
                                borderRadius: 3
                            },
                            '&:hover fieldset': {
                                border: `1px solid ${theme.palette.primary.main}`
                            },
                            '&.Mui-focused fieldset': {
                                border: `1px solid ${theme.palette.primary.main}`
                            },
                            '& input': {
                                py: 2,
                                fontWeight: 500
                            },
                            backgroundColor: 'white',
                            borderRadius: 3,
                            overflow: 'hidden',
                            backdropFilter: 'blur(10px)',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                            }
                        }
                    }}
                />
            </Grid>
            
            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Serial Number"
                    value={formData.equipment_serial}
                    onChange={(e) => handleEquipmentSerialChange(e.target.value)}
                    placeholder="Equipment serial number"
                    InputLabelProps={{
                        sx: { fontWeight: 500 }
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                border: `1px solid ${theme.palette.grey[400]}`,
                                borderRadius: 3
                            },
                            '&:hover fieldset': {
                                border: `1px solid ${theme.palette.primary.main}`
                            },
                            '&.Mui-focused fieldset': {
                                border: `1px solid ${theme.palette.primary.main}`
                            },
                            '& input': {
                                py: 2,
                                fontWeight: 500
                            },
                            backgroundColor: 'white',
                            borderRadius: 3,
                            overflow: 'hidden',
                            backdropFilter: 'blur(10px)',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                            }
                        }
                    }}
                />
            </Grid>
            
            <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                    <InputLabel sx={{ fontWeight: 500 }}>Calibration Status</InputLabel>
                    <Select
                        value={formData.calibration_status}
                        label="Calibration Status"
                        onChange={(e) => handleCalibrationStatusChange(e.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                                border: `1px solid ${theme.palette.grey[400]}`,
                                borderRadius: 3
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                border: `1px solid ${theme.palette.primary.main}`
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                border: `1px solid ${theme.palette.primary.main}`
                            },
                            '& .MuiSelect-select': {
                                py: 2,
                                fontWeight: 500
                            },
                            backgroundColor: 'white',
                            borderRadius: 3,
                            overflow: 'hidden',
                            backdropFilter: 'blur(10px)',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                            }
                        }}
                    >
                        <MenuItem value="calibrated" sx={{ py: 2, fontWeight: 500 }}>Calibrated</MenuItem>
                        <MenuItem value="due" sx={{ py: 2, fontWeight: 500 }}>Due Soon</MenuItem>
                        <MenuItem value="overdue" sx={{ py: 2, fontWeight: 500 }}>Overdue</MenuItem>
                        <MenuItem value="not_required" sx={{ py: 2, fontWeight: 500 }}>Not Required</MenuItem>
                    </Select>
                </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
                <DateTimePicker
                    label="Last Calibration Date"
                    value={formData.calibration_date}
                    onChange={(newValue) => handleCalibrationDateChange(newValue)}
                    format={batchTimeFormat === '24hour' ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY h:mm A'}
                    ampm={batchTimeFormat !== '24hour'}
                    slotProps={{
                        textField: {
                            fullWidth: true,
                            InputLabelProps: {
                                sx: { fontWeight: 500 }
                            },
                            sx: {
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        border: `1px solid ${theme.palette.grey[400]}`,
                                        borderRadius: 3
                                    },
                                    '&:hover fieldset': {
                                        border: `1px solid ${theme.palette.primary.main}`
                                    },
                                    '&.Mui-focused fieldset': {
                                        border: `1px solid ${theme.palette.primary.main}`
                                    },
                                    '& input': {
                                        py: 2,
                                        fontWeight: 500
                                    },
                                    backgroundColor: 'white',
                                    borderRadius: 3,
                                    overflow: 'hidden',
                                    backdropFilter: 'blur(10px)',
                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                        transform: 'translateY(-1px)',
                                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                                    }
                                }
                            }
                        }
                    }}
                />
            </Grid>
            
            <Grid item xs={12} md={3}>
                <DateTimePicker
                    label="Calibration Due Date"
                    value={formData.calibration_due_date}
                    onChange={(newValue) => handleCalibrationDueDateChange(newValue)}
                    format={batchTimeFormat === '24hour' ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY h:mm A'}
                    ampm={batchTimeFormat !== '24hour'}
                    slotProps={{
                        textField: {
                            fullWidth: true,
                            InputLabelProps: {
                                sx: { fontWeight: 500 }
                            },
                            sx: {
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        border: `1px solid ${theme.palette.grey[400]}`,
                                        borderRadius: 3
                                    },
                                    '&:hover fieldset': {
                                        border: `1px solid ${theme.palette.primary.main}`
                                    },
                                    '&.Mui-focused fieldset': {
                                        border: `1px solid ${theme.palette.primary.main}`
                                    },
                                    '& input': {
                                        py: 2,
                                        fontWeight: 500
                                    },
                                    backgroundColor: 'white',
                                    borderRadius: 3,
                                    overflow: 'hidden',
                                    backdropFilter: 'blur(10px)',
                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                        transform: 'translateY(-1px)',
                                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                                    }
                                }
                            }
                        }
                    }}
                />
            </Grid>

            {/* Environmental Summary */}
            {formData.room_temperature_c && formData.equipment_name && (
                <Grid item xs={12}>
                    <Alert 
                        severity="success" 
                        sx={{ 
                            mt: 2,
                            borderRadius: 2,
                            backgroundColor: alpha(theme.palette.success.main, 0.1),
                            border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                            '& .MuiAlert-icon': {
                                color: theme.palette.success.main
                            }
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                            ✓ Environmental Conditions Recorded
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                <strong>Temperature:</strong> {formData.room_temperature_c}°C 
                                {formData.room_humidity_percent && ` • Humidity: ${formData.room_humidity_percent}%`}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                <strong>Equipment:</strong> {formData.equipment_name}
                                {formData.equipment_id && ` (${formData.equipment_id})`}
                            </Typography>
                            {formData.clean_room_classification && (
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    <strong>Environment:</strong> {formData.clean_room_classification}
                                </Typography>
                            )}
                        </Box>
                    </Alert>
                </Grid>
            )}
            </Grid>
        </Box>
    );
} 